import React from "react";
import {
  BedDouble,
  Briefcase,
  Car,
  GraduationCap,
  Globe,
  HeartHandshake,
  MountainSnow,
  Plane,
  ShieldCheck,
  TrainFront,
  Wallet,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { useBookingModal } from "../context/BookingModalContext";
import domesticToursImage from "../assets/images/service-domestic-tours.jpg";
import internationalToursImage from "../assets/images/destination-europe.jpg";
import airTicketingImage from "../assets/images/service-air-ticketing.jpg";
import trainTicketingImage from "../assets/images/service-train-ticketing.jpg";
import travelInsuranceImage from "../assets/images/service-travel-insurance.jpg";
import forexImage from "../assets/images/service-forex.jpg";
import airportTransferImage from "../assets/images/service-airport-transfer.jpg";
import hotelBookingImage from "../assets/images/service-hotel-booking.jpg";

const services = [
  { id: 1, image: domesticToursImage, icon: MountainSnow },
  { id: 2, image: internationalToursImage, icon: Globe },
  { id: 3, image: airTicketingImage, icon: Plane },
  { id: 4, image: trainTicketingImage, icon: TrainFront },
  { id: 5, image: travelInsuranceImage, icon: ShieldCheck },
  { id: 6, image: forexImage, icon: Wallet },
  { id: 7, image: airportTransferImage, icon: Car },
  { id: 8, image: hotelBookingImage, icon: BedDouble },
];

const groupServices = [
  { id: 1, icon: Briefcase },
  { id: 2, icon: GraduationCap },
  { id: 3, icon: HeartHandshake },
];

const Services: React.FC = () => {
  const { t } = useTranslation();
  const { open } = useBookingModal();

  useDocumentMeta({
    title: t("services.metaTitle"),
    description: t("services.metaDescription"),
    path: "/services",
  });

  return (
    <div className="bg-sand-50 min-h-screen">
      {/* Hero Header */}
      <div className="relative bg-navy-950 py-24 text-white text-center overflow-hidden">
        <div className="absolute -top-20 right-10 w-80 h-80 rounded-full bg-brand-500/10 blur-3xl" />
        <div className="relative">
          <span className="inline-block border border-brand-400/40 bg-brand-500/10 text-brand-300 text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-[0.2em]">
            {t("services.eyebrow")}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold mt-2">{t("services.title")}</h1>
          <div className="w-16 h-[3px] bg-gradient-to-r from-brand-600 to-brand-400 mx-auto mt-6 rounded-full" />
          <p className="text-navy-200 mt-6 text-lg max-w-xl mx-auto px-6">
            {t("services.subtitle")}
          </p>
        </div>
      </div>

      {/* Service Cards */}
      <div className="max-w-7xl mx-auto px-6 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map(({ id, image, icon: Icon }) => {
            const title = t(`services.items.${id}.title`);
            const description = t(`services.items.${id}.description`);
            return (
              <div
                key={id}
                className="group rounded-2xl overflow-hidden bg-white shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-navy-900/5 flex flex-col sm:flex-row"
              >
                <div className="relative w-full sm:w-52 h-52 flex-shrink-0">
                  {image ? (
                    <>
                      <img
                        src={image}
                        alt={title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-950/60 to-navy-950/10" />
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-navy-800 to-navy-950 flex items-center justify-center">
                      <Icon size={40} className="text-brand-400/50" strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-brand-600 to-brand-400 text-navy-950 shadow-gold">
                    <Icon size={22} strokeWidth={2} />
                  </div>
                </div>

                <div className="p-6 flex flex-col justify-between flex-grow">
                  <div>
                    <h3 className="font-serif text-2xl font-bold text-navy-900 mb-2">{title}</h3>
                    <p className="text-navy-900/55 leading-relaxed text-sm sm:text-[15px]">
                      {description}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      open({
                        title,
                        subtitle: description,
                        badge: t("services.serviceInquiry"),
                        badgeClass: "bg-white/25 text-white",
                      })
                    }
                    className="mt-6 bg-gradient-to-r from-brand-600 to-brand-400 text-navy-950 font-bold text-sm px-6 py-2.5 rounded-full shadow-gold hover:-translate-y-0.5 hover:shadow-lg transition-all w-fit"
                  >
                    {t("services.enquireNow")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Corporate & Group Travel */}
      <div className="max-w-7xl mx-auto px-6 pb-16 sm:pb-20">
        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-navy-900 text-center mb-10">
          {t("services.groupHeading")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {groupServices.map(({ id, icon: Icon }) => {
            const title = t(`services.group.${id}.title`);
            const description = t(`services.group.${id}.description`);
            return (
              <div
                key={id}
                className="rounded-2xl bg-white shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-navy-900/5 p-7 flex flex-col"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-brand-600 to-brand-400 text-navy-950 shadow-gold mb-5">
                  <Icon size={22} strokeWidth={2} />
                </div>
                <h3 className="font-serif text-xl font-bold text-navy-900 mb-2">{title}</h3>
                <p className="text-navy-900/55 leading-relaxed text-sm flex-grow">{description}</p>
                <button
                  onClick={() =>
                    open({
                      title,
                      subtitle: description,
                      badge: t("services.groupInquiry"),
                      badgeClass: "bg-white/25 text-white",
                    })
                  }
                  className="mt-6 border-2 border-brand-600 text-brand-700 font-bold text-sm px-5 py-2.5 rounded-full hover:bg-brand-600 hover:text-white transition-all w-fit"
                >
                  {t("services.enquireNow")}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-4 text-white">
            {t("services.cta.title")}
          </h2>
          <p className="text-brand-100 mb-9 text-lg">
            {t("services.cta.subtitle")}
          </p>
          <button
            onClick={() => open({ title: t("services.cta.customTitle") })}
            className="bg-navy-950 text-brand-300 font-bold px-10 py-4 rounded-full hover:shadow-2xl hover:-translate-y-0.5 transition-all text-lg shadow-xl"
          >
            {t("services.cta.button")}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Services;
